/**
 * Bộ thu chẩn đoán phía trình duyệt (diagnostics.ts) — chuẩn EMS 7 dự án
 * Ref: PLAN-FEEDBACK-AUTO-DIAGNOSTICS-V1
 *
 * Thu vào bộ đệm vòng trong bộ nhớ (không lưu localStorage):
 * - Console: 50 dòng error/warn gần nhất
 * - Lỗi chưa bắt: window.error, unhandledrejection (message, stack 15 dòng đầu)
 * - Request lỗi: 30 request lỗi gần nhất (method, URL bỏ query nhạy cảm, status, duration, message, requestId)
 * - Chuỗi thao tác: 30 bước gần nhất (chuyển trang, bấm nút theo nhãn, mở hộp thoại) — không ghi nội dung ô nhập
 * - Môi trường: phiên bản app, trình duyệt, hệ điều hành, kích thước màn hình, ngôn ngữ, userId, vai trò, thời điểm
 */

import { isChromeDevToolsBug } from './devtools-shield';
import { isGatewayDownStatus } from './service-status';
import { changelogData } from '@/config/changelog-data';
import { useAuthStore } from '@/stores/auth.store';
import {
  getJournalEvents,
  getJournalSessionId,
  recordJournalEvent,
  startEventJournal,
  type JournalEvent,
} from './event-journal';

export interface ConsoleLogEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: string;
}

export interface UnhandledErrorEntry {
  type: 'error' | 'unhandledrejection' | 'boundary';
  message: string;
  stack?: string;
  route: string;
  timestamp: string;
}

export interface FailedRequestEntry {
  method: string;
  url: string;
  status: number | string;
  durationMs?: number;
  message?: string;
  requestId?: string;
  timestamp: string;
}

export interface BreadcrumbActionEntry {
  kind: 'navigate' | 'click' | 'dialog';
  label: string;
  route: string;
  timestamp: string;
}

export interface DiagnosticsEnvironment {
  appVersion: string;
  userAgent: string;
  platform: string;
  viewport: string;
  language: string;
  route: string;
  userId?: string;
  userRole?: string;
  capturedAt: string;
}

export interface DiagnosticsSnapshot {
  /** Chuẩn v2: mã phiên tab + 50 sự kiện gần nhất của nhật ký (route/click/submit/api/console/error) */
  sessionId?: string;
  events?: Omit<JournalEvent, '_s'>[];
  console: ConsoleLogEntry[];
  unhandledErrors: UnhandledErrorEntry[];
  failedRequests: FailedRequestEntry[];
  breadcrumbs: BreadcrumbActionEntry[];
  env: DiagnosticsEnvironment;
}

const MAX_CONSOLE_ENTRIES = 50;
const MAX_UNHANDLED_ERRORS = 20;
const MAX_FAILED_REQUESTS = 30;
const MAX_BREADCRUMBS = 30;
const MAX_STACK_LINES = 15;
const MAX_SNAPSHOT_BYTES = 64 * 1024;
const AUTO_REPORT_LIMIT_PER_MIN = 5;

const SENSITIVE_KEY_PATTERN = /(password|passwd|pwd|token|access_token|refresh_token|authorization|cookie|secret|otp|cccd|api_key|apikey)/gi;
const SENSITIVE_VALUE_IN_TEXT = /\b(password|passwd|token|accessToken|refreshToken|secret|otp|cccd)\s*["']?\s*[:=]\s*(['"]?)[^\s"',}&]+(\2)/gi;
const BEARER_PATTERN = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi;

const NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /AbortError/i,
  /The user aborted a request/i,
];

const consoleBuffer: ConsoleLogEntry[] = [];
const unhandledBuffer: UnhandledErrorEntry[] = [];
const requestBuffer: FailedRequestEntry[] = [];
const breadcrumbBuffer: BreadcrumbActionEntry[] = [];

// Theo dõi lỗi 5xx / network lặp lại >= 2 lần trong 1 phút
const recentServerFailureTimestamps = new Map<string, number[]>();
// Giới hạn tối đa 5 phiếu tự động / phút / phiên trình duyệt
let autoReportTimestamps: number[] = [];

type AutoReportSender = (payload: {
  source: 'AUTO_CLIENT';
  title: string;
  description: string;
  module?: string;
  pageUrl: string;
  priority: 'MEDIUM' | 'HIGH' | 'CRITICAL';
  fingerprint: string;
  diagnostics: DiagnosticsSnapshot;
  appVersion: string;
  userAgent: string;
}) => Promise<{ feedback?: { id: string; code: string } | null; skipped?: boolean } | void>;

let autoReportSender: AutoReportSender | null = null;
let installed = false;

export function pushRingBuffer<T>(buffer: T[], item: T, maxLen: number): void {
  buffer.push(item);
  if (buffer.length > maxLen) {
    buffer.splice(0, buffer.length - maxLen);
  }
}

export function maskSensitiveText(input: unknown): string {
  if (input === null || input === undefined) return '';
  let str = '';
  if (typeof input === 'string') {
    str = input;
  } else if (input instanceof Error) {
    str = `${input.name}: ${input.message}`;
  } else {
    try {
      str = JSON.stringify(input, (key, val) => {
        if (key && SENSITIVE_KEY_PATTERN.test(key)) return '[REDACTED]';
        return val;
      });
    } catch {
      str = String(input);
    }
  }
  return str
    .replace(BEARER_PATTERN, 'Bearer [REDACTED]')
    .replace(SENSITIVE_VALUE_IN_TEXT, '$1=[REDACTED]')
    .slice(0, 1500);
}

export function sanitizeUrl(rawUrl: string): string {
  if (!rawUrl) return '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(rawUrl, base);
    for (const key of Array.from(u.searchParams.keys())) {
      if (SENSITIVE_KEY_PATTERN.test(key) || key.toLowerCase() === 'input') {
        u.searchParams.set(key, '[REDACTED]');
      }
    }
    return `${u.pathname}${u.search}`;
  } catch {
    return rawUrl.split('?')[0].slice(0, 300);
  }
}

export function isNoiseMessage(msg: string, stack?: string): boolean {
  if (!msg && !stack) return true;
  if (isChromeDevToolsBug(msg) || isChromeDevToolsBug(stack)) return true;
  const combined = `${msg || ''}\n${stack || ''}`;
  return NOISE_PATTERNS.some((re) => re.test(combined));
}

export function trimStack(stack?: string): string | undefined {
  if (!stack) return undefined;
  return stack
    .split('\n')
    .slice(0, MAX_STACK_LINES)
    .map((line) => maskSensitiveText(line))
    .join('\n');
}

export function computeClientFingerprint(
  messageOrInput:
    | string
    | {
        source?: string;
        route?: string;
        errorName?: string;
        message?: string;
        firstFrame?: string;
      },
  firstFrame?: string,
  route?: string,
): string {
  const message =
    typeof messageOrInput === 'object' && messageOrInput !== null
      ? `${messageOrInput.errorName || ''}:${messageOrInput.message || ''}`
      : messageOrInput;
  const frame =
    typeof messageOrInput === 'object' && messageOrInput !== null
      ? messageOrInput.firstFrame
      : firstFrame;
  const routeVal =
    typeof messageOrInput === 'object' && messageOrInput !== null
      ? messageOrInput.route
      : route;

  const normMsg = String(message || 'unknown')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<uuid>')
    .replace(/\bc[a-z0-9]{20,30}\b/gi, '<cuid>')
    .replace(/\b\d{4,}\b/g, '<num>')
    .trim()
    .slice(0, 240);
  const normFrame = String(frame || '')
    .replace(/:\d+:\d+/g, '')
    .replace(/\b([A-Za-z0-9_.]+?)-[A-Za-z0-9_-]{6,12}\.(js|mjs|cjs|css)\b/g, '$1.<chunk>.$2')
    .trim()
    .slice(0, 160);
  const normRoute = normalizeRoutePattern(routeVal);
  const raw = `${normMsg}|${normFrame}|${normRoute}`;
  // FNV-1a 64-bit style dual 32-bit hash for browser compatibility without async crypto
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw.charCodeAt(i);
    h1 ^= ch;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= ch;
    h2 = Math.imul(h2, 0x85ebca6b);
  }
  return `fp_${(h1 >>> 0).toString(16).padStart(8, '0')}${(h2 >>> 0).toString(16).padStart(8, '0')}`;
}

export function getAppVersion(): string {
  return changelogData?.[0]?.version || '1.8.0';
}

export const getCurrentAppVersion = getAppVersion;
export const redactSensitiveText = maskSensitiveText;
export const isIgnorableNoiseError = isNoiseMessage;

export function normalizeRoutePattern(route?: string): string {
  return String(route || '/')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/c[a-z0-9]{20,30}/gi, '/:id')
    .replace(/\/\d+/g, '/:id')
    .trim()
    .slice(0, 120);
}

export function sanitizeUrlPath(rawUrl: string): string {
  if (!rawUrl) return '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(rawUrl, base);
    return u.pathname;
  } catch {
    return rawUrl.split('?')[0].slice(0, 300);
  }
}

function currentRoute(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
}

export function recordConsoleEntry(level: 'error' | 'warn', args: unknown[]): void {
  const message = args.map((a) => maskSensitiveText(a)).join(' ').trim();
  if (!message || isNoiseMessage(message)) return;
  pushRingBuffer(
    consoleBuffer,
    {
      level,
      message: message.slice(0, 1000),
      timestamp: new Date().toISOString(),
    },
    MAX_CONSOLE_ENTRIES,
  );
  recordJournalEvent({ kind: 'console', name: message, level, payload: { message } });
}

export function recordBreadcrumb(
  kindOrObj:
    | BreadcrumbActionEntry['kind']
    | { type?: BreadcrumbActionEntry['kind']; kind?: BreadcrumbActionEntry['kind']; label: string; route?: string },
  label?: string,
  route?: string,
): void {
  const kind =
    typeof kindOrObj === 'object' && kindOrObj !== null
      ? kindOrObj.kind || kindOrObj.type || 'click'
      : kindOrObj;
  const rawLabel =
    typeof kindOrObj === 'object' && kindOrObj !== null ? kindOrObj.label : label || '';
  const rawRoute =
    typeof kindOrObj === 'object' && kindOrObj !== null ? kindOrObj.route : route;
  const cleanLabel = maskSensitiveText(rawLabel).replace(/\s+/g, ' ').trim().slice(0, 140);
  if (!cleanLabel) return;
  pushRingBuffer(
    breadcrumbBuffer,
    {
      kind,
      label: cleanLabel,
      route: rawRoute || currentRoute(),
      timestamp: new Date().toISOString(),
    },
    MAX_BREADCRUMBS,
  );
}

export function recordFailedRequest(
  entry: Omit<FailedRequestEntry, 'timestamp' | 'url'> & { url: string; errorMessage?: string },
): void {
  const statusNum = Number(entry.status);
  // Bỏ qua 401/403 hết phiên hoặc request tự báo lỗi feedback
  if (statusNum === 401 || statusNum === 403) return;
  if (entry.url.includes('feedback.reportAuto') || entry.url.includes('feedback.create')) return;
  const msgText = entry.message || entry.errorMessage || '';
  if (msgText && isNoiseMessage(msgText)) return;

  const cleanUrl = sanitizeUrl(entry.url);
  const item: FailedRequestEntry = {
    method: (entry.method || 'GET').toUpperCase(),
    url: cleanUrl,
    status: entry.status,
    durationMs: entry.durationMs,
    message: msgText ? maskSensitiveText(msgText).slice(0, 500) : undefined,
    requestId: entry.requestId,
    timestamp: new Date().toISOString(),
  };
  pushRingBuffer(requestBuffer, item, MAX_FAILED_REQUESTS);

  // Tự động tạo phiếu nếu API trả 5xx hoặc lỗi mạng lặp >= 2 lần trong 1 phút
  // 502/503/504 = máy chủ đang cập nhật / khởi động lại: không mở phiếu từ trình duyệt (phiếu "mất kết nối" theo ngày do máy chủ tự gộp)
  const is5xxOrNet =
    ((Number.isFinite(statusNum) && statusNum >= 500 && !isGatewayDownStatus(statusNum)) || entry.status === 'NETWORK_ERROR');
  if (is5xxOrNet) {
    const key = `${item.method}:${cleanUrl}:${entry.status}`;
    const now = Date.now();
    const list = (recentServerFailureTimestamps.get(key) || []).filter((t) => now - t < 60_000);
    list.push(now);
    recentServerFailureTimestamps.set(key, list);
    if (list.length >= 2) {
      recentServerFailureTimestamps.set(key, []);
      void triggerAutoClientFeedback({
        title: `Lỗi kết nối API (${entry.status}) tại ${cleanUrl}`,
        description: `Yêu cầu ${item.method} ${cleanUrl} thất bại ${list.length} lần liên tiếp (${item.message || entry.status}).`,
        priority: 'HIGH',
        firstFrame: cleanUrl,
      });
    }
  }
}

export function recordUnhandledError(
  type: UnhandledErrorEntry['type'],
  errorOrMessage: unknown,
  explicitStack?: string,
): Promise<{ id: string; code: string } | null> {
  const rawMessage =
    errorOrMessage instanceof Error
      ? errorOrMessage.message
      : typeof errorOrMessage === 'string'
        ? errorOrMessage
        : maskSensitiveText(errorOrMessage);
  const rawStack =
    explicitStack || (errorOrMessage instanceof Error ? errorOrMessage.stack : undefined);

  if (!rawMessage || isNoiseMessage(rawMessage, rawStack)) return Promise.resolve(null);

  const entry: UnhandledErrorEntry = {
    type,
    message: maskSensitiveText(rawMessage).slice(0, 500),
    stack: trimStack(rawStack),
    route: currentRoute(),
    timestamp: new Date().toISOString(),
  };
  pushRingBuffer(unhandledBuffer, entry, MAX_UNHANDLED_ERRORS);
  recordJournalEvent({
    kind: 'error',
    name: entry.message,
    level: 'error',
    payload: { type, route: entry.route, stack: entry.stack?.split('\n').slice(0, 4).join('\n') },
  });

  // Không tự động mở phiếu AUTO_CLIENT cho lỗi HTTP 4xx từ tRPC (đã hiển thị toast validation cho người dùng)
  const httpStatus = Number((errorOrMessage as any)?.data?.httpStatus || (errorOrMessage as any)?.status || 0);
  if (type === 'unhandledrejection' && httpStatus >= 400 && httpStatus < 500) {
    return Promise.resolve(null);
  }

  const firstFrame = entry.stack?.split('\n')?.[1] || entry.route;
  return triggerAutoClientFeedback({
    title: entry.message,
    description: `Lỗi kỹ thuật chưa bắt (${type}) tại trang ${entry.route}: ${entry.message}`,
    priority: type === 'boundary' ? 'HIGH' : 'MEDIUM',
    firstFrame,
  });
}

export function isAutoReportEnabled(): boolean {
  if (typeof window !== 'undefined' && (window as any).__ENABLE_AUTO_FEEDBACK__ !== undefined) {
    return Boolean((window as any).__ENABLE_AUTO_FEEDBACK__);
  }
  const envVal = (import.meta as any).env?.VITE_ENABLE_AUTO_FEEDBACK;
  if (envVal === 'true' || envVal === '1') return true;
  if (envVal === 'false' || envVal === '0') return false;
  return Boolean((import.meta as any).env?.PROD);
}

export function registerAutoReportSender(sender: AutoReportSender): void {
  autoReportSender = sender;
}

export async function triggerAutoClientFeedback(params: {
  title: string;
  description?: string;
  priority?: 'MEDIUM' | 'HIGH' | 'CRITICAL';
  firstFrame?: string;
  force?: boolean;
}): Promise<{ id: string; code: string } | null> {
  if (!params.force && !isAutoReportEnabled()) return null;
  if (!autoReportSender) return null;

  const now = Date.now();
  autoReportTimestamps = autoReportTimestamps.filter((t) => now - t < 60_000);
  if (autoReportTimestamps.length >= AUTO_REPORT_LIMIT_PER_MIN) {
    return null;
  }
  autoReportTimestamps.push(now);

  const snapshot = getDiagnosticsSnapshot();
  const route = snapshot.env.route;
  const fp = computeClientFingerprint(params.title, params.firstFrame, route);

  try {
    const res = await autoReportSender({
      source: 'AUTO_CLIENT',
      title: params.title.slice(0, 180),
      description: params.description || params.title,
      pageUrl: route,
      priority: params.priority || 'HIGH',
      fingerprint: fp,
      diagnostics: snapshot,
      appVersion: snapshot.env.appVersion,
      userAgent: snapshot.env.userAgent,
    });
    if (res && 'feedback' in res && res.feedback) {
      return { id: res.feedback.id, code: res.feedback.code };
    }
  } catch {
    // Không để lỗi gửi phiếu tự động gây vòng lặp lỗi
  }
  return null;
}

export function getDiagnosticsSnapshot(): DiagnosticsSnapshot {
  const user = useAuthStore.getState()?.user;
  const env: DiagnosticsEnvironment = {
    appVersion: getAppVersion(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    platform: typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown',
    viewport:
      typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '0x0',
    language: typeof navigator !== 'undefined' ? navigator.language || 'vi' : 'vi',
    route: currentRoute(),
    userId: user?.id,
    userRole: user?.role,
    capturedAt: new Date().toISOString(),
  };

  const snapshot: DiagnosticsSnapshot = {
    sessionId: getJournalSessionId(),
    events: getJournalEvents(50),
    console: [...consoleBuffer],
    unhandledErrors: [...unhandledBuffer],
    failedRequests: [...requestBuffer],
    breadcrumbs: [...breadcrumbBuffer],
    env,
  };

  // Đảm bảo kích thước bản chẩn đoán <= 64 KB
  try {
    let serialized = JSON.stringify(snapshot);
    if (serialized.length > MAX_SNAPSHOT_BYTES) {
      snapshot.console = snapshot.console.slice(-15);
      snapshot.breadcrumbs = snapshot.breadcrumbs.slice(-15);
      snapshot.failedRequests = snapshot.failedRequests.slice(-10);
      snapshot.events = (snapshot.events || []).slice(-30);
      serialized = JSON.stringify(snapshot);
      if (serialized.length > MAX_SNAPSHOT_BYTES) {
        snapshot.console = snapshot.console.slice(-5);
        snapshot.events = (snapshot.events || []).slice(-15);
      }
    }
  } catch {
    // Ignore
  }

  return snapshot;
}

export function getDiagnosticsSummaryText(snapshot?: DiagnosticsSnapshot): string {
  const s = snapshot || getDiagnosticsSnapshot();
  const errCount = s.console.filter((c) => c.level === 'error').length + s.unhandledErrors.length;
  const apiCount = s.failedRequests.length;
  const actionCount = s.breadcrumbs.length;
  return `${errCount} lỗi console, ${apiCount} API lỗi, ${actionCount} thao tác gần nhất`;
}

export function getSuggestedTitleFromDiagnostics(snapshot?: DiagnosticsSnapshot): string {
  const s = snapshot || getDiagnosticsSnapshot();
  if (s.unhandledErrors.length > 0) {
    return s.unhandledErrors[s.unhandledErrors.length - 1].message.slice(0, 120);
  }
  if (s.failedRequests.length > 0) {
    const lastReq = s.failedRequests[s.failedRequests.length - 1];
    return (lastReq.message || `Lỗi API ${lastReq.status} (${lastReq.url})`).slice(0, 120);
  }
  const lastConsoleError = [...s.console].reverse().find((c) => c.level === 'error');
  if (lastConsoleError) {
    return lastConsoleError.message.slice(0, 120);
  }
  return '';
}

export function clearDiagnosticsBuffers(): void {
  consoleBuffer.length = 0;
  unhandledBuffer.length = 0;
  requestBuffer.length = 0;
  breadcrumbBuffer.length = 0;
  recentServerFailureTimestamps.clear();
  autoReportTimestamps = [];
}

export function installDiagnosticsCollector(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1. Bọc console.error và console.warn
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    try {
      recordConsoleEntry('error', args);
    } catch {
      // ignore
    }
    origError(...args);
  };

  console.warn = (...args: unknown[]) => {
    try {
      recordConsoleEntry('warn', args);
    } catch {
      // ignore
    }
    origWarn(...args);
  };

  // 2. Bắt window.error và unhandledrejection
  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      recordUnhandledError('error', event.error || event.message);
    } catch {
      // ignore
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      recordUnhandledError('unhandledrejection', event.reason);
    } catch {
      // ignore
    }
  });

  // 3. Ghi nhận thao tác click trên nút / liên kết / menu (không ghi nội dung input)
  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      try {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        const clickable = target.closest(
          'button, a, [role="button"], [role="tab"], [role="menuitem"], [data-testid]',
        ) as HTMLElement | null;
        if (!clickable) return;
        if (clickable.closest('[data-feedback-ignore="true"]')) return;
        const tag = clickable.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

        const label =
          clickable.getAttribute('aria-label') ||
          clickable.getAttribute('title') ||
          clickable.textContent?.trim() ||
          clickable.getAttribute('data-testid') ||
          tag;
        if (label) {
          recordBreadcrumb('click', `${tag}: ${label.slice(0, 60)}`);
        }
        // Nhật ký v2: chữ hiển thị ≤ 60 ký tự hoặc data-testid — không bao giờ ghi giá trị ô nhập
        const testId = clickable.getAttribute('data-testid') || undefined;
        const hasEditable = !!clickable.querySelector('input, textarea, select, [contenteditable="true"]');
        const visible = hasEditable
          ? ''
          : (clickable.getAttribute('aria-label') || clickable.getAttribute('title') || clickable.textContent || '')
              .replace(/\s+/g, ' ')
              .trim()
              .slice(0, 60);
        const journalLabel = visible || testId || tag;
        recordJournalEvent({
          kind: 'click',
          name: journalLabel,
          payload: { tag, ...(testId ? { testId } : {}), route: currentRoute() },
        });
      } catch {
        // ignore
      }
    },
    { capture: true, passive: true },
  );

  // 4. Ghi submit form (tên form) — không ghi dữ liệu trong form
  document.addEventListener(
    'submit',
    (event: Event) => {
      try {
        const form = event.target as HTMLFormElement | null;
        if (!form || form.tagName?.toLowerCase() !== 'form') return;
        if (form.closest('[data-feedback-ignore="true"]')) return;
        const formName =
          form.getAttribute('name') ||
          form.getAttribute('data-testid') ||
          form.getAttribute('aria-label') ||
          form.id ||
          'form';
        recordJournalEvent({ kind: 'submit', name: formName.slice(0, 60), payload: { route: currentRoute() } });
      } catch {
        // ignore
      }
    },
    { capture: true },
  );

  // 5. Nhật ký sự kiện v2: gửi lô định kỳ + khi rời trang
  startEventJournal();

  // Cho phép E2E test truy cập bộ thu chẩn đoán trực tiếp mà không làm nổ anti-fake-fixture console.error listener
  (window as any).__APV_DIAGNOSTICS__ = {
    recordConsoleEntry,
    recordBreadcrumb,
    recordFailedRequest,
    recordUnhandledError,
    getDiagnosticsSnapshot,
    triggerAutoClientFeedback,
    recordJournalEvent,
    getJournalEvents,
    getJournalSessionId,
  };
}
